type FileType =
  | "image"
  | "docx"
  | "spreadsheet"
  | "pdf"
  | "csv"
  | "html"
  | "unknown";

interface AMapperResolverDefault {
  name: "default";
  href: string;
}

export interface AMapperResolverFile {
  name: "file";
  fileType: FileType;
  href: string;
}

interface AMapperResolverEmbedComps {
  name: "embed-comps";
  href: string;
}

interface AMapperResolverIntegrations {
  name: "integrations";
  href: string;
}

interface AMapperResolverQRCode {
  name: "qrcode";
  href: string;
}

type AMapperResolver =
  | AMapperResolverDefault
  | AMapperResolverFile
  | AMapperResolverEmbedComps
  | AMapperResolverIntegrations
  | AMapperResolverQRCode;

const ACCOUNT_SETTINGS_PATHNAME = "/account/settings";
const ACCOUNT_CONNECTED_APPS_PATHNAME_OLD = "/account/connected-apps"; // Keep for backward compatibility
const ACCOUNT_INTEGRATIONS_PATHNAME = "/account/integrations";
const SETTINGS_MODAL_SEARCH_PARAM = "?settings_modal=integrations";
const FILE_STORAGE_DOMAIN_PREFIX = "https://av8-sidekick-backend";

function getFileType(href: string | undefined | null): FileType {
  const isFiledHostedBySidekick =
    href && href.startsWith(FILE_STORAGE_DOMAIN_PREFIX);

  if (!isFiledHostedBySidekick) return "unknown";

  const fileExtensionToType: Record<string, FileType> = {
    // ".jpg": "image",
    // ".jpeg": "image",
    // ".png": "image",
    ".docx": "docx",
    ".doc": "docx",
    ".xlsx": "spreadsheet",
    ".xls": "spreadsheet",
    ".pdf": "pdf",
    ".csv": "csv",
    // ".html": "html",
    // ".xhtml": "html",
  };

  const extension = href
    .slice(href.lastIndexOf("."))
    .toLowerCase() as keyof typeof fileExtensionToType;

  return fileExtensionToType[extension] || "unknown";
}

const getConnectedAppsUrl = (href: string): string | null => {
  const url = getOriginUrlIfSameDomain(href);

  if (
    url &&
    [
      ACCOUNT_CONNECTED_APPS_PATHNAME_OLD,
      ACCOUNT_INTEGRATIONS_PATHNAME,
      ACCOUNT_SETTINGS_PATHNAME,
    ].includes(url.pathname)
  ) {
    return SETTINGS_MODAL_SEARCH_PARAM;
  }

  return null;
};

const domains = ["app.sidekickgpt.io", "app.staging.sidekickgpt.dev"];

const getOriginUrlIfSameDomain = (href: string): URL | null => {
  try {
    const url = new URL(href);

    if (window.location.origin === url.origin) {
      return url;
    }

    if (domains.some((domain) => url.host === domain)) {
      return new URL(`${window.location.origin}${url.pathname}`);
    }

    return null;
  } catch {
    return null;
  }
};

// DISABLED BECAUSE WE DON'T USE IT
// const getMapperByDisplayFormat = (href: string) => {
//   try {
//     const urlObj = new URL(href);

//     const displayFormats: Array<AMapperResolverQRCode["name"]> = ["qrcode"];

//     const queryString = (urlObj.searchParams
//       .get("display_format")
//       ?.toLowerCase()
//       .trim() ?? "") as AMapperResolverQRCode["name"];

//     if (displayFormats.includes(queryString)) {
//       urlObj.searchParams.delete("display_format");
//       return {
//         name: queryString,
//         href: urlObj.origin + urlObj.pathname + urlObj.search,
//       };
//     }
//     return null;
//   } catch (e) {
//     console.error("Error in getMapperByDisplayFormat:", e);
//     return null;
//   }
// };

export const getAMapperResolver = (href: string): AMapperResolver => {
  const fileType = getFileType(href);

  if (fileType !== "unknown") {
    return {
      name: "file",
      fileType,
      href,
    };
  }

  const connectedAppsUrl = getConnectedAppsUrl(href);
  if (connectedAppsUrl) {
    return {
      name: "integrations",
      href: connectedAppsUrl,
    };
  }

  // const displayFormatUrl = getMapperByDisplayFormat(href);
  // if (displayFormatUrl) return displayFormatUrl;

  return {
    name: "default",
    href,
  };
};
